import { Box, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import { Link } from '#features/routing';
import { Button, Image } from '#features/ui';

type GameConnectProps = {
  gameName: string;
  /** Admin `connectImage` - optional. The band renders text-only (centered) when absent. */
  image?: string | null;
  accentColor: string;
};

/**
 * Game-specific "connect" band. Renders on every game page (not gated on the image) so each
 * landing carries a block of unique, keyword-rich prose - the SEO body copy that covers
 * "team finder / group finder / looking for group / duo" beyond the hero's "<Game> LFG".
 * Uses the admin `connectImage` as the visual when present; otherwise centres the copy.
 */
export const GameConnect = ({ gameName, image, accentColor }: GameConnectProps) => {
  const { t } = useLingui();

  const copy = (
    <Stack
      gap={5}
      flex="1"
      alignItems={{ base: 'center', md: image ? 'flex-start' : 'center' }}
      textAlign={{ base: 'center', md: image ? 'left' : 'center' }}
    >
      <Text fontSize="sm" fontWeight="700" color={accentColor} textTransform="uppercase" letterSpacing="0.08em">
        {gameName}
      </Text>
      <Heading
        as="h2"
        fontSize={{ base: '2xl', md: '4xl' }}
        fontWeight="800"
        letterSpacing="-0.02em"
        lineHeight="1.05"
        css={{ textWrap: 'balance' }}
      >
        {t`Your next ${gameName} squad is one post away`}
      </Heading>
      <Text fontSize={{ base: 'md', md: 'lg' }} color="fg.muted" lineHeight="1.7" maxW={image ? '480px' : '680px'}>
        {t`Looking for a ${gameName} team finder that gets you the right squad? Teamplay is a free LFG and group finder built around how ${gameName} is really played. Filter by rank, role and region, then post once - or browse players looking for group right now. Whether you want a duo for ranked or a full stack for the weekend, you'll find teammates in minutes. No Discord hopping - post your ${gameName} LFG, get matched, and jump into voice in one click.`}
      </Text>
      <Button size="lg" minW="200px" style={{ boxShadow: `0 0 26px ${accentColor}55` }} asChild>
        <Link to="/{-$locale}/sign-up">{t`Find teammates`}</Link>
      </Button>
    </Stack>
  );

  if (!image) {
    return (
      <Box maxW="720px" mx="auto" w="100%">
        {copy}
      </Box>
    );
  }

  return (
    <Flex direction={{ base: 'column', md: 'row' }} alignItems="center" gap={{ base: 8, md: 12 }} w="100%">
      <Box w={{ base: '100%', md: '46%' }} maxW={{ base: '420px', md: '520px' }} position="relative" flexShrink={0}>
        <Box
          position="absolute"
          inset="-10%"
          background={`radial-gradient(circle at center, ${accentColor}22 0%, transparent 70%)`}
          pointerEvents="none"
          zIndex={0}
        />
        <Image
          src={image}
          sizes="(min-width: 768px) 520px, 90vw"
          alt={`${gameName} - Teamplay.gg`}
          loading="lazy"
          style={{
            objectFit: 'contain',
            width: '100%',
            height: 'auto',
            position: 'relative',
            zIndex: 1,
            // Fade the bottom edge into the section background (no hard cut).
            WebkitMaskImage: 'linear-gradient(to bottom, #000 0%, #000 72%, transparent 98%)',
            maskImage: 'linear-gradient(to bottom, #000 0%, #000 72%, transparent 98%)',
          }}
        />
      </Box>
      {copy}
    </Flex>
  );
};
